export type SystemEventName =
  | "UserLoggedIn"
  | "UserLoggedOut"
  | "HealthCheckCompleted"
  | "IncidentOpened"
  | "AgentStarted"
  | "AgentFinished"
  | "TaskCreated"
  | "TaskCompleted"
  | "MemorySaved"
  | "KnowledgeGenerated"
  | "ProductOpened"
  | "ProductRegistered"
  | "DepartmentMirrored"
  | "CommandExecuted"
  | "CommandFailed";

export type SystemEvent<TPayload = unknown> = {
  id: string;
  name: SystemEventName;
  payload: TPayload;
  timestamp: string;
  correlationId: string;
};

export type EventHandler<TPayload = unknown> = (
  event: SystemEvent<TPayload>,
) => Promise<void> | void;
export type EventMiddleware = (
  event: SystemEvent,
  next: () => Promise<void>,
) => Promise<void>;

type Subscription = {
  unsubscribe: () => void;
};

type HandlerRegistration = {
  priority: number;
  retries: number;
  handler: EventHandler;
};

export class EventBus {
  private readonly handlers = new Map<SystemEventName, HandlerRegistration[]>();
  private readonly middleware: EventMiddleware[] = [];

  subscribe<TPayload>(
    name: SystemEventName,
    handler: EventHandler<TPayload>,
    options: { priority?: number; retries?: number } = {},
  ): Subscription {
    const registration: HandlerRegistration = {
      priority: options.priority ?? 0,
      retries: options.retries ?? 0,
      handler: handler as EventHandler,
    };
    const handlers = this.handlers.get(name) ?? [];
    handlers.push(registration);
    handlers.sort((a, b) => b.priority - a.priority);
    this.handlers.set(name, handlers);

    return {
      unsubscribe: () => {
        this.handlers.set(
          name,
          (this.handlers.get(name) ?? []).filter(
            (item) => item !== registration,
          ),
        );
      },
    };
  }

  use(middleware: EventMiddleware): void {
    this.middleware.push(middleware);
  }

  async publish<TPayload>(event: SystemEvent<TPayload>): Promise<void> {
    const runHandlers = async () => {
      for (const registration of this.handlers.get(event.name) ?? []) {
        await this.runHandler(registration, event as SystemEvent);
      }
    };

    let pipeline: () => Promise<void> = runHandlers;

    for (const middleware of [...this.middleware].reverse()) {
      const next = pipeline;
      pipeline = () => middleware(event as SystemEvent, next);
    }

    await pipeline();
  }

  private async runHandler(
    registration: HandlerRegistration,
    event: SystemEvent,
  ): Promise<void> {
    let attempt = 0;

    while (true) {
      try {
        await registration.handler(event);
        return;
      } catch (error) {
        if (attempt >= registration.retries) {
          throw error;
        }
        attempt += 1;
      }
    }
  }
}
